// src/components/layout/Header.js
import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getProfile } from "../../api/userApi";
import { FiMenu, FiX, FiLogOut, FiUser, FiHome, FiFileText, FiDollarSign, FiUsers, FiMail, FiChevronDown } from "react-icons/fi";
import { FaShieldAlt, FaCrown, FaMedal, FaStar, FaUserCheck } from "react-icons/fa";

function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const userId = user.user_id;

  useEffect(() => {
    async function loadProfile() {
      try {
        setLoading(true);
        const data = await getProfile(userId);
        setProfile(data);
      } catch (err) {
        console.error(err);
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    }

    loadProfile();
  }, [userId]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getStatusConfig = (status) => {
    const statusMap = {
      NEW: {
        color: "from-red-500 to-red-600",
        bgColor: "bg-red-50",
        textColor: "text-red-700",
        borderColor: "border-red-300",
        icon: FaShieldAlt,
        label: "New"
      },
      ACTIVATED: {
        color: "from-green-500 to-green-600",
        bgColor: "bg-green-50",
        textColor: "text-green-700",
        borderColor: "border-green-300",
        icon: FaUserCheck,
        label: "Activated"
      },
      BRONZE: {
        color: "from-orange-600 to-amber-700",
        bgColor: "bg-orange-50",
        textColor: "text-orange-800",
        borderColor: "border-orange-400",
        icon: FaMedal,
        label: "Bronze"
      },
      SILVER: {
        color: "from-gray-400 to-gray-500",
        bgColor: "bg-gray-100",
        textColor: "text-gray-700",
        borderColor: "border-gray-400",
        icon: FaStar,
        label: "Silver"
      },
      GOLD: {
        color: "from-yellow-400 to-yellow-600",
        bgColor: "bg-yellow-50",
        textColor: "text-yellow-800",
        borderColor: "border-yellow-400",
        icon: FaCrown,
        label: "Gold"
      },
    };
    return statusMap[status] || statusMap.NEW;
  };

  const navLinks = [
    { to: "/", label: "Home", end: true, icon: FiHome },
    { to: "/my-reviews", label: "My Reviews", end: true, icon: FiFileText },
    { to: "/wallet", label: "Wallet", end: false, icon: FiDollarSign },
    { to: "/refer", label: "Refer", end: false, icon: FiUsers },
    { to: "/contact", label: "Contact", end: false, icon: FiMail },
  ];

  const statusConfig = profile ? getStatusConfig(profile.status_level) : null;
  const StatusIcon = statusConfig?.icon;

  return (
    <header className="sticky top-0 z-50 bg-white shadow-lg border-b-2 border-primary-100">
      <div className="bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 h-1"></div>

      <div className="mx-auto max-w-7xl px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo - Enhanced */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <div className="absolute -inset-2 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl opacity-20 group-hover:opacity-40 blur-md transition duration-300"></div>
              <div className="relative bg-gradient-to-br from-primary-50 to-secondary-50 p-2 rounded-xl border-2 border-primary-200 group-hover:border-primary-400 transition-all">
                <img
                  src="/logo.png"
                  alt="Review Market EU"
                  className="h-9 w-9 object-contain"
                />
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
                Review Market EU
              </h1>
              <p className="text-xs text-gray-500 font-medium">Earn by Reviewing</p>
            </div>
          </Link>

          {/* Desktop Navigation - Enhanced */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const LinkIcon = link.icon;
              return (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.end}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all duration-200 ${
                      isActive
                        ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md"
                        : "text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <LinkIcon className={`h-4 w-4 ${isActive ? "animate-pulse" : ""}`} />
                      <span>{link.label}</span>
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>

          {/* User Info & Actions - Enhanced */}
          <div className="hidden lg:flex items-center gap-3">
            {profile && statusConfig && (
              <div className={`flex items-center gap-3 px-4 py-2 rounded-xl border-2 ${statusConfig.borderColor} ${statusConfig.bgColor}`}>
                <div className="text-right">
                  <p className="text-sm font-bold text-gray-800">
                    {profile.user_name}
                  </p>
                  <div className="flex items-center gap-1.5 justify-end">
                    <StatusIcon className={`h-3 w-3 ${statusConfig.textColor}`} />
                    <span className={`text-xs font-semibold ${statusConfig.textColor}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                </div>
              </div>
            )}

            <Link
              to="/profile"
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 text-primary-700 font-medium transition-all shadow-sm hover:shadow-md"
            >
              <FiUser className="h-4 w-4" />
              <span>Profile</span>
            </Link>

            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 text-red-700 font-medium transition-all shadow-sm hover:shadow-md"
            >
              <FiLogOut className="h-4 w-4" />
              <span>Logout</span>
            </button>
          </div>

          {/* Mobile Menu Button - Enhanced */}
          <button
            className="lg:hidden p-2.5 rounded-lg bg-primary-50 hover:bg-primary-100 text-primary-700 transition-all"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <FiX className="h-6 w-6" />
            ) : (
              <FiMenu className="h-6 w-6" />
            )}
          </button>
        </div>

        {/* Mobile Menu - Enhanced */}
        {mobileMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 animate-slideUp">
            <div className="flex flex-col gap-2 bg-gradient-to-br from-gray-50 to-primary-50 rounded-xl p-3 border border-gray-200">
              {navLinks.map((link) => {
                const LinkIcon = link.icon;
                return (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    end={link.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                        isActive
                          ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md"
                          : "text-gray-700 hover:bg-white hover:text-primary-600 hover:shadow-sm"
                      }`
                    }
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <LinkIcon className="h-5 w-5" />
                    <span>{link.label}</span>
                  </NavLink>
                );
              })}

              <div className="border-t-2 border-gray-200 my-2"></div>

              {profile && statusConfig && (
                <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 ${statusConfig.borderColor} ${statusConfig.bgColor}`}>
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${statusConfig.color}`}>
                    <StatusIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-800">
                      {profile.user_name}
                    </p>
                    <p className={`text-xs font-semibold ${statusConfig.textColor}`}>
                      {statusConfig.label} Member
                    </p>
                  </div>
                </div>
              )}

              <Link
                to="/profile"
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gradient-to-r from-primary-50 to-primary-100 hover:from-primary-100 hover:to-primary-200 text-primary-700 font-medium transition-all shadow-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                <FiUser className="h-5 w-5" />
                <span>Profile</span>
              </Link>

              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="flex items-center gap-3 px-4 py-3 rounded-lg bg-gradient-to-r from-red-50 to-red-100 hover:from-red-100 hover:to-red-200 text-red-700 font-medium transition-all shadow-sm"
              >
                <FiLogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}

export default Header;
